/**
 * AdvancedMarker を MarkerClusterer でまとめるためのフック
 *
 * 返り値の register を各 AdvancedMarker の ref コールバックで呼ぶと、
 * マーカーがクラスタラーに登録され、アンマウント時に自動で外れる。
 * 大量マーカーの登録・解除が同時に起きても描画は 1 フレームに 1 回へまとめる。
 */

import { type Cluster, MarkerClusterer, type Renderer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer'
import { useMap } from '@vis.gl/react-google-maps'
import { useCallback, useEffect, useRef } from 'react'

type ClusterMarker = google.maps.marker.AdvancedMarkerElement

type UseMarkerClustererOptions = {
  /** このズーム以下でクラスタリングする（超えると個別表示） */
  maxZoom: number
  /** クラスタにまとめる半径（ピクセル） */
  radius?: number
}

/** 件数バッジの直径（ピクセル） */
const getClusterSize = (count: number) => {
  if (count >= 100) return 44
  if (count >= 10) return 36
  return 30
}

/** 個別マーカーより手前に出す */
const CLUSTER_Z_INDEX_BASE = 1000

const clusterRenderer: Renderer = {
  render: ({ count, position }: Cluster) => {
    const size = getClusterSize(count)
    const content = document.createElement('div')
    content.className =
      'flex items-center justify-center rounded-full border-2 border-white bg-blue-600/85 text-xs font-semibold text-white shadow-md'
    content.style.width = `${size}px`
    content.style.height = `${size}px`
    // AdvancedMarkerElement のアンカーは下端中央なので、円の中心を座標に合わせる
    content.style.transform = 'translateY(50%)'
    content.textContent = String(count)

    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content,
      zIndex: CLUSTER_Z_INDEX_BASE + count,
    })
  },
}

export function useMarkerClusterer({ maxZoom, radius = 60 }: UseMarkerClustererOptions) {
  const map = useMap()
  const markersRef = useRef(new Set<ClusterMarker>())
  const clustererRef = useRef<MarkerClusterer | null>(null)
  const frameRef = useRef<number | null>(null)

  const scheduleRender = useCallback(() => {
    if (frameRef.current !== null) return
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null
      clustererRef.current?.render()
    })
  }, [])

  useEffect(() => {
    if (!map) return

    const clusterer = new MarkerClusterer({
      map,
      markers: Array.from(markersRef.current),
      algorithm: new SuperClusterAlgorithm({ maxZoom, radius }),
      renderer: clusterRenderer,
    })
    clustererRef.current = clusterer

    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
        frameRef.current = null
      }
      clusterer.clearMarkers(true)
      clusterer.setMap(null)
      clustererRef.current = null
    }
  }, [map, maxZoom, radius])

  /** AdvancedMarker の ref コールバックから呼ぶ。返り値は ref のクリーンアップとして使う */
  const register = useCallback(
    (marker: ClusterMarker) => {
      markersRef.current.add(marker)
      clustererRef.current?.addMarker(marker, true)
      scheduleRender()

      return () => {
        markersRef.current.delete(marker)
        clustererRef.current?.removeMarker(marker, true)
        scheduleRender()
      }
    },
    [scheduleRender],
  )

  return register
}
